import { faExternalLink, faPlus } from '@fortawesome/pro-regular-svg-icons';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icon, Spinner, Text } from '@feathr/components';
import { useLocalUrl } from '@feathr/extender/state';

import * as styles from './DomainAvailabilityStates.css';

interface IDomainAvailabilityStatesProps {
  isLoading: boolean;
  hasAvailableDomains: boolean;
}

/**
 * Reusable component for displaying loading and empty states for domain availability
 */
function DomainAvailabilityStates({
  isLoading,
  hasAvailableDomains,
}: Readonly<IDomainAvailabilityStatesProps>): JSX.Element | null {
  const { t } = useTranslation();
  const localUrl = useLocalUrl();

  // Loading domains
  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <Spinner size={32} />
      </div>
    );
  }

  // No domains available
  if (!hasAvailableDomains) {
    return (
      <div className={styles.emptyState}>
        <Text.LargeSemibold className={styles.emptyStateTitle}>
          {t('No domains have been authorized')}
        </Text.LargeSemibold>
        <Text.DefaultRegular className={styles.emptyStateDescription}>
          {t('Authorize a domain to continue')}
        </Text.DefaultRegular>
        <Button
          href={localUrl('/settings/account/domains')}
          prefix={<Icon icon={faPlus} />}
          suffix={<Icon icon={faExternalLink} />}
          target={'_blank'}
          type={'secondary'}
        >
          {t('Add domain')}
        </Button>
      </div>
    );
  }

  return null;
}

export default DomainAvailabilityStates;
